import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Stack,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import type { ShiftEndConfirmation } from '@/hooks/useShiftEndConfirmation';

const EXTENSION_CHOICES = [15, 30, 45, 60, 90, 120, 180, 240];

interface ShiftEndPromptDialogProps {
  confirmation: ShiftEndConfirmation;
  locale: string;
  isPending: boolean;
  onContinue: (extensionMinutes: number, explanation: string) => void;
  onStop: () => void;
  onExplain: (explanation: string, clockOutAt?: string) => void;
  onDismiss: () => void;
}

function formatTime(iso: string, locale: string): string {
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

function localDateTime(iso: string): string {
  const date = new Date(iso);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function ShiftEndPromptDialog({
  confirmation,
  locale,
  isPending,
  onContinue,
  onStop,
  onExplain,
  onDismiss,
}: ShiftEndPromptDialogProps) {
  const { t } = useTranslation('hr');
  const [extensionMinutes, setExtensionMinutes] = useState(30);
  const [explanation, setExplanation] = useState('');
  const [clockOutAt, setClockOutAt] = useState(localDateTime(confirmation.scheduledShiftEnd));
  const isAutoClosed = confirmation.status === 'auto_closed';
  const collectExplanationOnly =
    confirmation.status !== 'pending' &&
    (confirmation.needsEmployeeExplanation || isAutoClosed);

  useEffect(() => {
    setExtensionMinutes(30);
    setExplanation('');
    setClockOutAt(localDateTime(confirmation.scheduledShiftEnd));
  }, [confirmation.id, confirmation.status, confirmation.scheduledShiftEnd]);

  const handleContinue = () => {
    onContinue(extensionMinutes, explanation);
  };

  const handleExplain = () => {
    if (!isAutoClosed) {
      onExplain(explanation);
      return;
    }
    const corrected = new Date(clockOutAt);
    if (Number.isFinite(corrected.getTime())) onExplain(explanation, corrected.toISOString());
  };

  return (
    <ResponsiveDialog
      open
      fullWidth
      maxWidth="sm"
      mobileTitle={
        isAutoClosed || collectExplanationOnly
          ? t('shiftEndPrompt.autoClosedTitle')
          : t('shiftEndPrompt.title')
      }
      onClose={onDismiss}
    >
      <DialogTitle>
        {isAutoClosed || collectExplanationOnly
          ? t('shiftEndPrompt.autoClosedTitle')
          : t('shiftEndPrompt.title')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography>
            {isAutoClosed
              ? t('shiftEndPrompt.autoClosedBody', {
                  time: formatTime(confirmation.scheduledShiftEnd, locale),
                })
              : collectExplanationOnly
                ? t('shiftEndPrompt.autoClosedExplanation')
                : t('shiftEndPrompt.body', {
                    time: formatTime(confirmation.scheduledShiftEnd, locale),
                  })}
          </Typography>
          {isAutoClosed && (
            <TextField
              label={t('shiftEndPrompt.correctedClockOut')}
              type="datetime-local"
              value={clockOutAt}
              onChange={(event) => setClockOutAt(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              required
            />
          )}
          {!collectExplanationOnly && (
            <FormControl fullWidth>
              <InputLabel id="shift-end-extension-label">{t('shiftEndPrompt.extension')}</InputLabel>
              <Select
                labelId="shift-end-extension-label"
                label={t('shiftEndPrompt.extension')}
                value={extensionMinutes}
                onChange={(event) => setExtensionMinutes(Number(event.target.value))}
              >
                {EXTENSION_CHOICES.map((minutes) => (
                  <MenuItem key={minutes} value={minutes}>
                    {minutes % 60 === 0 && minutes >= 60
                      ? t('shiftEndPrompt.hours', { count: minutes / 60 })
                      : t('shiftEndPrompt.minutes', { count: minutes })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            label={
              collectExplanationOnly
                ? t('shiftEndPrompt.autoClosedExplanation')
                : t('shiftEndPrompt.explanation')
            }
            value={explanation}
            onChange={(event) => setExplanation(event.target.value.slice(0, 2000))}
            multiline
            minRows={3}
            fullWidth
            required
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        {collectExplanationOnly ? (
          <Button
            variant="contained"
            onClick={handleExplain}
            disabled={isPending || explanation.trim().length < 3 || (isAutoClosed && !clockOutAt)}
          >
            {t('shiftEndPrompt.submitExplanation')}
          </Button>
        ) : (
          <>
            <Button onClick={onStop} disabled={isPending} color="error">
              {t('shiftEndPrompt.stop')}
            </Button>
            <Button
              variant="contained"
              onClick={handleContinue}
              disabled={isPending || explanation.trim().length < 3}
            >
              {t('shiftEndPrompt.submitContinue')}
            </Button>
          </>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
